import * as React from "react";
import {
    InfoLabel,
    makeStyles,
    Text,
    Title3,
    Subtitle2,
    tokens,
    useId,
} from "@fluentui/react-components";
import { CardSection } from "../../components/CardSection";

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        padding: "2rem",
        width: "80%",
        margin: "0 auto",
        maxWidth: "1400px"
    },
    card: {
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: "12px",
        boxShadow: tokens.shadow16,
        padding: "1.5rem",
    },
    tierCardGrid: {
        display: "flex",
        gap: "1.5rem",
        flexWrap: "wrap",
        justifyContent: "center",
    },
    tierCard: {
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: "10px",
        flex: "1 1 280px",
        minWidth: "240px",
        padding: "1rem",
        boxShadow: tokens.shadow4,
    },
    infoLabel: {
        color: tokens.colorNeutralForeground3,
        marginTop: "0.5rem",
    },
});

export const Id = "a6bf499e-aab8-406b-84e8-d5bebd653c30";
export const Title = "Pricing";
export const Subtitle = "Find out about platform fees and costs";
export const Description = "Find out about pricing tiers";
export const Keywords = ["cost", "platform", "fee", "tier"];

export const Component: React.FC = React.memo(() => {
    const styles = useStyles();
    const selfServiceSpanId = useId("selfservice");

    return (
        <div className={styles.container}>
            <CardSection title="Overview">
                <Text>
                    Ianus Guard keeps pricing simple and transparent.
                    <br />
                    <br />
                    <strong>Your user account is always free</strong> - costs are only tied to the publishers you manage.
                    <br />
                    <br />
                    Each publisher is assigned a service tier:
                    <ul>
                        <li>Trial</li>
                        <li>Basic</li>
                        <li>Premium</li>
                        <li>Enterprise</li>
                    </ul>
                    New publishers start on the <strong>Trial</strong> tier by default. Upgrade anytime as your business grows.
                    <br />
                    <br />
                    <strong>Team collaboration is free</strong> - the number of users in a publisher doesn’t affect pricing.
                </Text>
            </CardSection>

            <Title3>Service Tiers</Title3>
            <div className={styles.tierCardGrid}>
                <div id="trial" className={styles.tierCard}>
                    <Subtitle2>Trial</Subtitle2>
                    <ul>
                        <li>Free</li>
                        <li>Max 1 customer</li>
                        <li>Max 3 products</li>
                        <li>Free short-lived licenses (valid up to one month)</li>
                        <li>20€ (excl. tax) per license having a validity longer than one month</li>
                    </ul>
                </div>

                <div id="basic" className={styles.tierCard}>
                    <Subtitle2>Basic</Subtitle2>
                    <ul>
                        <li>15€ (excl. tax) / month</li>
                        <li>Unlimited customers</li>
                        <li>Max 3 products</li>
                        <li>Free short-lived licenses (valid up to one month)</li>
                        <li>20€ (excl. tax) per license having a validity longer than one month</li>
                    </ul>
                </div>

                <div id="premium" className={styles.tierCard}>
                    <Subtitle2>Premium</Subtitle2>
                    <ul>
                        <li>500€ (excl. tax) / month</li>
                        <li>Unlimited customers</li>
                        <li>Unlimited products</li>
                        <li>Unlimited licenses without additional costs</li>
                        <li>Telemetry API for usage tracking in customer organizations (limited to one product)</li>
                    </ul>
                </div>

                <div id="enterprise" className={styles.tierCard}>
                    <Subtitle2>Enterprise</Subtitle2>
                    <ul>
                        <li>1000€ (excl. tax) / month</li>
                        <li>Unlimited customers</li>
                        <li>Unlimited products</li>
                        <li>Unlimited licenses without additional costs</li>
                        <li>Telemetry API for usage tracking in customer organizations (for all products)</li>
                        <li>
                            <InfoLabel
                                htmlFor={selfServiceSpanId}
                                className={styles.infoLabel}
                                info="Offer landing pages for customers to generate demo licenses."
                            >
                                <Text id={selfServiceSpanId}>Customer self-service</Text>
                            </InfoLabel>
                        </li>
                    </ul>
                </div>
            </div>

            <CardSection title="Trial Tier">
                <Text>
                    The Trial tier is the default tier, designed for evaluating Ianus Guard. You can only create one customer which is usually your own company.
                    <br />
                    Upgrade to Basic or higher to manage additional customers.
                    <br />
                    <br />
                    <strong>Price:</strong> Free
                    <br />
                    <br />
                    <strong>Included:</strong>
                    <ul>
                        <li>Full access to core licensing features</li>
                        <li>Free short-lived licenses (up to one month validity)</li>
                        <li>Pay-per-license model for licenses valid longer than one month</li>
                    </ul>
                    <strong>Limitations:</strong>
                    <ul>
                        <li>Max 1 customer</li>
                        <li>Max 3 products</li>
                        <li>No access to customer self-service or advanced automation features</li>
                    </ul>
                </Text>
            </CardSection>

            <CardSection title="Basic Tier">
                <Text>
                    The Basic tier is ideal for independent developers and small publishers getting started.
                    <br />
                    <br />
                    <strong>Price:</strong> 15€ / month (excl. tax)
                    <br />
                    <br />
                    <strong>Included:</strong>
                    <ul>
                        <li>Full access to core licensing features</li>
                        <li>Free short-lived licenses (up to one month validity)</li>
                        <li>Unlimited amount of customers</li>
                        <li>Pay-per-license model for licenses valid longer than one month</li>
                    </ul>
                    <strong>Limitations:</strong>
                    <ul>
                        <li>Max 3 products</li>
                        <li>No access to customer self-service or advanced automation features</li>
                    </ul>
                </Text>
            </CardSection>

            <CardSection title="Premium Tier">
                <Text>
                    Premium is best for publishers who issue licenses regularly and want predictable costs.
                    <br />
                    <br />
                    Premium includes everything from Basic, plus:
                    <ul>
                        <li>Unlimited products</li>
                        <li>Unlimited issuance of licenses without additional costs</li>
                        <li>Telemetry API for usage tracking in customer organizations (limited to one product)</li>
                    </ul>
                    <strong>Limitation:</strong>
                    <ul>
                        <li>Telemetry API limited to one product</li>
                        <li>No access to customer self-service</li>
                    </ul>
                    <br />
                    <strong>Price: 500€ / month (excl. tax)</strong>
                </Text>
            </CardSection>

            <CardSection title="Enterprise Tier">
                <Text>
                    Enterprise includes everything from Premium, plus:
                    <ul>
                        <li>Public-facing landing pages for demo license generation</li>
                        <li>Telemetry upload to send user count from customer organizations to the Ianus Guard portal</li>
                    </ul>
                    <br />
                    Ideal for established companies requiring automation and scale.
                    <br />
                    <br />
                    <strong>Price: 1000€ / month (excl. tax)</strong>
                </Text>
            </CardSection>
        </div>
    );
});

